import { useSelector } from "react-redux";
import { Typography, Box } from "@material-ui/core";

import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from "recharts";

import { ShaDowBox } from "../boxes";
import { NoDataCard } from "./noDataCard";
import { COLORS } from "../../theme/colors";
import { Tooltip } from "../charts/Tooltip";
import { TooltipContent } from "../charts/TooltipContent";
import { currencyFormatter, percentValue } from "../../services";

const rightLabel = (props, label, currency, isRental = true, fill = COLORS.darks[0][100]) => {
  const { x, y, height, value } = props;

  if (!value) return null;

  return (
    <g>
      <text
        x={x}
        y={y}
        dy={isRental ? height / 2 - 12 : height + 30}
        dx={55}
        fill={COLORS.darks[0][100]}
        fontSize={12}
      >
        {label}
      </text>
      <text
        x={x}
        y={y}
        dy={isRental ? height / 2 + 12 : height + 54}
        dx={55}
        fill={fill}
        style={{ fontSize: 24, fontWeight: 700 }}
      >
        {currencyFormatter(value, currency)}
      </text>
    </g>
  );
};

const leftLabel = (props, label, rental) => {
  const { x, y, height, value } = props;

  if (
    !value ||
    (["Physical vacancy", "Economic vacancy"].includes(label) &&
      Math.abs(value) < percentValue(rental, 20))
  )
    return null;

  return (
    <text x={x} y={y} dy={height / 2} dx={-130} fill={COLORS.darks[0][100]}>
      {label}
    </text>
  );
};

export const GrossRentalRevenues = ({ chartData }) => {
  const title = "Gross Rental Revenues";
  const currencySymbol = useSelector((state) => state.auth.user.currency.symbol);

  if (!chartData.length) return <NoDataCard title={title} />;

  return (
    <ShaDowBox fullHeight>
      <Box style={{ padding: 24 }}>
        <Typography variant="h4">{title}</Typography>
        <Box style={{ marginTop: 24, height: 280 }}>
          <ResponsiveContainer>
            <ComposedChart
              width="100%"
              height="100%"
              data={chartData}
              stackOffset="sign"
              margin={0}
            >
              <Tooltip
                wrapperStyle={{ outline: "none" }}
                cursor={false}
                chartData={chartData}
                content={<TooltipContent chartData={chartData} hideSum />}
              />
              <XAxis dataKey="name" scale="point" hide={true} />
              <YAxis hide={true} />
              <ReferenceLine y={0} stroke="#000" />
              <Area
                type="monotone"
                dataKey="positive"
                fill={COLORS.purples[10]}
                stroke={COLORS.purples[10]}
                chartType="hide"
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="negative"
                fill="rgba(225, 201, 167, 0.2)"
                stroke="rgba(225, 201, 167, .2)"
                chartType="hide"
                activeDot={false}
              />
              <Bar dataKey="rental" barSize={30} fill={COLORS.blue} stackId="stack">
                <LabelList
                  dataKey="rental"
                  content={(props) => rightLabel(props, "Rental", currencySymbol)}
                />
              </Bar>
              <Bar
                dataKey="economic_vacancy"
                barSize={30}
                fill="#E4DCD1"
                stackId="stack"
                stroke={COLORS.black}
                strokeDasharray="3 3"
              >
                <LabelList
                  dataKey="economic_vacancy"
                  content={(props) => leftLabel(props, "Economic vacancy", chartData[1].rental)}
                />
                <LabelList
                  dataKey="total_vacancy"
                  content={(props) =>
                    rightLabel(props, "Unrealized rental", currencySymbol, false, COLORS.red)
                  }
                />
              </Bar>
              <Bar
                dataKey="physical_vacancy"
                barSize={30}
                fill="#D3CCC1"
                stackId="stack"
                stroke={COLORS.black}
                strokeDasharray="3 3"
              >
                <LabelList
                  dataKey="physical_vacancy"
                  content={(props) => leftLabel(props, "Physical vacancy", chartData[1].rental)}
                />
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </ShaDowBox>
  );
};
